import { useEffect, useRef, useState } from "react"
import { Button, Heading, Text } from "@smooth/design-system-components"
import { evaluationError } from "../errors"
import { excerpt, lastCustomerMessage, pointTimeLabel } from "../flows"
import type { Flow, FlowPoint } from "../flows"
import { UNSET_SHORT_NAME, isUnsetTerminal } from "../../terminals"
import { observedTerminal, terminalMatch } from "../types"
import type { Terminals } from "../types"
import { useEvaluationBatches } from "../batch/useEvaluationBatches"
import { CustomerName } from "./FlowList"
import { AlertMessage, StatusMessage, TerminalBadge } from "./parts"
import { PromptFields, usePromptChoice } from "./PromptPicker"

type Props = {
  flow: Flow
  sourceUsers: Record<string, string | null>
  terminals: Terminals
  /** 開いている時点のケース */
  selectedId: number | null
  disabled: boolean
  onChoose: (point: FlowPoint) => void
  /** 流れを丸ごと生成し終えたら一覧（各時点の最新の生成）を読み直す */
  onFlowGenerated: () => void
}

/** 時点ごとの AI の結果（期待と比べた一言）。生成していなければ「未生成」 */
function PointResult({ point, terminals }: { point: FlowPoint; terminals: Terminals }) {
  const { record } = point
  const run = record.latestRun
  if (!run) return <Text size="sm">AI：未生成</Text>
  if (run.status !== "completed")
    return <Text size="sm">AI：{run.status === "running" ? "生成中" : "生成失敗"}</Text>
  const observed = observedTerminal(run)
  const match = terminalMatch({
    expectedTerminal: record.expectedTerminal,
    generationMetadata: run.generationMetadata,
  })
  const label =
    match === "match" || match === "body_match"
      ? "期待どおり"
      : match === "mismatch"
        ? "期待と違う"
        : match === "unset"
          ? "—"
          : "記録なし"
  return (
    <span className="inline-flex flex-wrap items-baseline gap-2 text-caption text-text-default">
      AI：{observed ? <TerminalBadge id={observed} mismatch={match === "mismatch"} /> : "—"}
      {observed && terminals[observed] ? <span>{terminals[observed]}</span> : null}
      <span className={match === "mismatch" ? "font-bold text-text-error" : "text-text-muted"}>
        {label}
      </span>
      {run.caseVersion !== record.lockVersion && (
        <span className="text-text-muted">（データを変える前の生成）</span>
      )}
    </span>
  )
}

/** 流れを丸ごとまとめて生成する（プロンプトを選び、起点からつながる時点をすべて流す） */
function FlowBatch({ flow, onFinished }: { flow: Flow; onFinished: () => void }) {
  const choice = usePromptChoice()
  const [batchId, setBatchId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)
  const batches = useEvaluationBatches(batchId)
  const detail = batches.detail.data
  const running = detail?.data.status === "running"
  const finished = !!detail && detail.data.status !== "running"

  // 終わったときに1回だけ知らせる（onFinished は親の再レンダーごとに別の関数になり得るので、依存に入れない）
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished
  useEffect(() => {
    if (finished) onFinishedRef.current()
  }, [finished, batchId])

  const start = async () => {
    if (!choice.prompt) return
    setWorking(true)
    setError(null)
    try {
      const created = await batches.start({ ...choice.prompt, rootCaseId: flow.root.id })
      setBatchId(created.id)
    } catch (e) {
      setError(evaluationError(e))
    } finally {
      setWorking(false)
    }
  }

  return (
    <details className="border-t border-border-default">
      <summary className="cursor-pointer py-2 text-body font-bold text-text-default">
        この流れをまとめて生成（{flow.points.length}時点）
      </summary>
      <div className="flex flex-col gap-3 pb-3">
        <div className="grid gap-4 min-[600px]:grid-cols-2">
          <PromptFields choice={choice} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={start} disabled={!choice.prompt || working || running}>
            この流れをまとめて生成
          </Button>
          <Text as="span" size="sm">
            各時点の入力はそのままで、選んだプロンプトで生成し直します。LINE送信は行いません。
          </Text>
        </div>
        {running && detail && (
          <StatusMessage>
            生成中です（{detail.summary.finished}/{detail.summary.total}）。
          </StatusMessage>
        )}
        {finished && detail && (
          <StatusMessage>生成しました（{detail.summary.generated}時点）。</StatusMessage>
        )}
        {error && <AlertMessage>{error}</AlertMessage>}
      </div>
    </details>
  )
}

/**
 * お客様の流れのタイムライン。1人のお客様の会話を時点（検証ケース）ごとに並べ、各時点の最後のお客様の発言・期待する対応・
 * AI の対応と文面・メモ（確認ポイント）を見比べる。枝分かれ（同じ時点から返事を変えた続き）は字下げして並べる
 */
export function FlowTimeline({
  flow,
  sourceUsers,
  terminals,
  selectedId,
  disabled,
  onChoose,
  onFlowGenerated,
}: Props) {
  return (
    <section aria-label="流れのタイムライン" className="mb-4 flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Heading level={2}>{flow.root.title}</Heading>
        {/* 取り込み元の督促ケースが無い流れは、見出し（起点のケース名）がそのままお客様の呼び名なので出さない */}
        {flow.root.sourceDunningCaseId && (
          <Text size="sm">
            <CustomerName
              customer={{
                sourceDunningCaseId: flow.root.sourceDunningCaseId,
                sourceUserId: flow.root.sourceUserId ?? null,
              }}
              sourceUsers={sourceUsers}
            />
          </Text>
        )}
      </div>
      <ol className="flex flex-col">
        {flow.points.map((point) => {
          const { record } = point
          const message = lastCustomerMessage(record)
          const run = record.latestRun
          const selected = record.id === selectedId
          const aiText = run?.generationMetadata?.humanReviewRequired
            ? `人手対応：${run.generationMetadata.humanReviewReason ?? ""}`
            : run?.body
          return (
            <li key={record.id} style={{ marginLeft: `${point.depth * 1.6}rem` }}>
              <button
                type="button"
                aria-current={selected || undefined}
                aria-label={`${point.number}時点目 ${record.title}`}
                disabled={disabled}
                onClick={() => onChoose(point)}
                className={[
                  "flex w-full flex-col gap-1 border-b border-l-[3px] border-b-border-default px-3 py-2.5 text-left text-body text-text-default",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed",
                  selected
                    ? "border-l-action-primary bg-bg-info"
                    : "border-l-border-default hover:bg-bg-muted",
                ].join(" ")}
              >
                <span className="flex flex-wrap items-baseline gap-x-3">
                  <span className="font-bold">
                    {point.number}時点目 · {pointTimeLabel(record.scenario.asOf)}
                  </span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {message
                      ? `お客様「${excerpt(message.body || "（画像）", 60)}」`
                      : "お客様の発言なし（当社から送る時点）"}
                  </span>
                </span>
                <span className="inline-flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <span className="inline-flex items-baseline gap-2 text-caption">
                    期待：
                    {record.expectedTerminal ? (
                      <>
                        <TerminalBadge id={record.expectedTerminal} />
                        <span>
                          {terminals[record.expectedTerminal] ??
                            (isUnsetTerminal(record.expectedTerminal) ? UNSET_SHORT_NAME : "")}
                        </span>
                      </>
                    ) : (
                      "未設定"
                    )}
                  </span>
                  <PointResult point={point} terminals={terminals} />
                </span>
                {aiText && (
                  <span className="text-caption text-text-muted [overflow-wrap:anywhere]">
                    AIの文面：{excerpt(aiText, 90)}
                  </span>
                )}
                {record.checkpoints.trim() && (
                  <span className="text-caption text-text-muted [overflow-wrap:anywhere]">
                    メモ：{excerpt(record.checkpoints, 90)}
                  </span>
                )}
              </button>
            </li>
          )
        })}
      </ol>
      <FlowBatch flow={flow} onFinished={onFlowGenerated} />
    </section>
  )
}
